<template>
  <div v-if="isIosPlatform" class="apple-login-wrapper">
    <button 
      @click="handleAppleLogin" 
      :disabled="loading"
      class="apple-login-btn"
      type="button"
    >
      <span v-if="!loading" class="apple-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="16" height="18">
          <path fill="#ffffff" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
        </svg>
      </span>
      <span v-if="loading" class="spinner"></span>
      <span class="button-text">
        {{ loading ? 'Loading...' : buttonText }}
      </span>
    </button>
  </div>
</template>

<script>
import appleAuthService from '@/services/appleAuth';
import { Capacitor } from '@capacitor/core';

export default {
  name: 'AppleLoginButton',

  props: {
    buttonText: {
      type: String,
      default: 'Sign in with Apple'
    }
  },

  data() {
    return {
      loading: false,
      error: null,
      initialized: false
    };
  },

  computed: {
    isNative() {
      return Capacitor.isNativePlatform();
    },
    isIosPlatform() {
      return Capacitor.getPlatform() === 'ios';
    }
  },

  async mounted() {
    if (!this.isIosPlatform) {
      return;
    }

    try {
      await appleAuthService.initialize();
      this.initialized = true;
    } catch (e) {
      console.warn('Apple login initialization warning:', e);
    }
  },

  methods: {
    async handleAppleLogin() {
      this.error = null;
      this.loading = true;

      try {
        console.log('Starting Apple login...');
        const authData = await appleAuthService.login();
        console.log('Apple login successful, emitting login-success event');
        this.$emit('login-success', authData);
      } catch (error) {
        console.error('Apple login error:', error);
        const rawMsg = error.message || '';
        const lower = rawMsg.toLowerCase();
        if (lower.includes('cancel') || lower.includes('1001') || lower.includes('1000')) {
          this.error = 'Apple Sign-In failed: Cancelled by user.';
          error.message = 'Apple Sign-In failed: Cancelled by user.';
          error.code = 'CANCELED';
        } else {
          this.error = error.message || 'Failed to login with Apple';
        }
        this.$emit('login-error', error);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.apple-login-wrapper {
  width: 100%;
  margin-top: 10px;
}

/* Official Apple HIG Button Styling */
.apple-login-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 24px;
  background-color: #000000;
  border: 1px solid #000000;
  border-radius: 4px;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 12px;
}

.apple-login-btn:hover:not(:disabled) {
  background-color: #1a1a1a;  
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.apple-login-btn:active:not(:disabled) {
  background-color: #333333;
}

.apple-login-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.apple-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.button-text {
  flex: 1;
  text-align: center;
  letter-spacing: -0.2px;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
